import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { Users, ChevronRight, Minimize2, Maximize2 ,X, ChevronLeft } from 'lucide-react';
import ReusableTable from './ReusableTable';
import '../../styles/ReusableProjectTable.css';
import { getRequest } from '../services/apiService';

const ReusableProjectTable = ({ onDashboardOpen, selectedProject, selectedShip }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProjectState, setSelectedProjectState] = useState(null);
  const [selectedShipState, setSelectedShipState] = useState(null);
  const [showShipsDialog, setShowShipsDialog] = useState(false);
  const [showYardsDialog, setShowYardsDialog] = useState(false);
  const [showSubtasksDialog, setShowSubtasksDialog] = useState(false);
  const [showSubtaskDetailsDialog, setShowSubtaskDetailsDialog] = useState(false);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const [subtaskDetails, setSubtaskDetails] = useState([]);
  const [subtaskDetailsLoading, setSubtaskDetailsLoading] = useState(false);
  const [isFullView, setIsFullView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });
  const [first, setFirst] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tasksFirst, setTasksFirst] = useState(0);
  const [tasksRows, setTasksRows] = useState(10);
  const [tasksTotalRecords, setTasksTotalRecords] = useState(0);
  const [subtasksFirst, setSubtasksFirst] = useState(0);
  const [subtasksRows, setSubtasksRows] = useState(10);
  const [subtasksTotalRecords, setSubtasksTotalRecords] = useState(0);
  const [selectedTaskState, setSelectedTaskState] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const colorScheme = {
    headerBgColor: '#1e40af',
    headerTextColor: '#ffffff',
    tableBgColor: '#f3f4f6',
    tableTextColor: '#1f2937',
  };

  const fetchProjects = async (searchQuery = '', page = 1) => {
    setLoading(true);
    try {
      const response = await getRequest(`transaction/active-projects/?page=${page}`);
      const data = response.data || response;
      
      // Handle both old and new API responses
      let projectsData = [];
      let totalPages = 1;
      
      if (data.projects) {
        // New API response structure
        projectsData = data.projects;
        totalPages = data.total_pages || 1;
      } else if (Array.isArray(data)) {
        // Old API response structure (fallback)
        projectsData = data;
        totalPages = Math.ceil(data.length / rowsPerPage);
      }
      
      const mappedProjects = projectsData.map(project => ({
        id: project.project_id,
        name: project.project_name,
        progress: project.progress_percentage
      }));
      
      setProjects(mappedProjects);
      setTotalRecords(mappedProjects.length);
      setTotalPages(totalPages);
      setCurrentPage(page);
    } catch (error) {
      setProjects([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects(search, currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setCurrentPage(1);
      fetchProjects(search, 1);
    }, 700);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getDialogDimensions = () => {
    const isMobile = windowSize.width < 768;
    const isTablet = windowSize.width >= 768 && windowSize.width < 1024;
    
    if (isFullView) {
      return {
        width: '95vw',
        height: '90vh',
        maxWidth: '1800px',
        maxHeight: '900px'
      };
    }
    
    if (isMobile) {
      return {
        width: '95vw',
        height: 'auto',
        maxHeight: '80vh'
      };
    }
    
    if (isTablet) {
      return {
        width: '80vw',
        height: 'auto',
        maxHeight: '80vh'
      };
    }
    
    return {
      width: '60vw',
      height: 'auto',
      maxHeight: '80vh'
    };
  };

  const handleProjectClick = async (project) => {
    try {
      setLoading(true);
      const response = await getRequest(`transaction/active-projects/?project_id=${project.id}`);
      const formattedShips = response.map(ship => ({
        id: ship.ship_id,
        name: ship.ship_name,
        progress: ship.progress_percentage
      }));
      setSelectedProjectState({ ...project, ships: formattedShips });
      setShowShipsDialog(true);
    } catch (error) {
      console.error('Error fetching ships:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShipClick = async (ship) => {
  try {
    setLoading(true);
    const response = await getRequest(`transaction/active-projects/?ship_id=${ship.id}`);
    
    // Ensure we're mapping the task_id correctly
    const formattedTasks = response.map(task => ({
      id: task.task_id,  // Changed from yardNo to id
      name: task.task_name,
      progress: task.progress_percentage
    }));
    
    setSelectedShipState({ ...ship, yards: formattedTasks });
    setShowShipsDialog(false);
    setShowYardsDialog(true);
    setTasksFirst(0);
    setTasksTotalRecords(formattedTasks.length);
  } catch (error) {
    console.error('Error fetching tasks:', error);
  } finally {
    setLoading(false);
  }
};

  const handleYardClick = async (task) => {
  try {
    setLoading(true);
    
    // Ensure we're using the correct task ID property (yardNo)
    const taskId = task.yardNo || task.id;
    
    // Make API call with both ship_id and task_id
    const response = await getRequest(
      `transaction/active-projects/?ship_id=${selectedShipState.id}&task_id=${taskId}`
    );

    const formattedSubtasks = (response.data || response || []).map(subtask => ({
      id: subtask.sub_task_id,
      name: subtask.sub_task_name,
      progress: subtask.progress_percentage
    }));
    
    setSelectedTaskState({ 
      ...task, 
      subtasks: formattedSubtasks 
    });
    setSubtasksTotalRecords(formattedSubtasks.length);
    setShowYardsDialog(false);
    setShowSubtasksDialog(true);
  } catch (error) {
    console.error('Error fetching subtasks:', error);
  } finally {
    setLoading(false);
  }
};

  // New function to handle subtask details
  const handleSubtaskDetailsClick = async (subtask) => {
    try {
      setSubtaskDetailsLoading(true);
      setSelectedSubtask(subtask);
      
      // Using the subtask id as task_id for the API call
      const response = await getRequest(
        `transaction/active-projects/?ship_id=${selectedShipState.id}&task_id=${subtask.id}`
      );
      
      const details = response.data || response || [];
      setSubtaskDetails(details);
      setShowSubtaskDetailsDialog(true);
      
    } catch (error) {
      console.error('Error fetching subtask details:', error);
      setSubtaskDetails([]);
      setShowSubtaskDetailsDialog(true);
    } finally {
      setSubtaskDetailsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedShipState?.yards) {
      setTasksTotalRecords(selectedShipState.yards.length);
    }
  }, [selectedShipState]);

  const onTasksPageChange = (event) => {
    setTasksFirst(event.first);
    setTasksRows(event.rows);
  };

  const toggleFullView = () => {
    setIsFullView(!isFullView);
  };

  const progressTemplate = (rowData) => {
    const isMobile = windowSize.width < 640;
    return (
      <div className="flex items-center">
        <ProgressBar 
          value={rowData.progress} 
          showValue={false} 
          style={{ width: isMobile ? '5rem' : '10rem', height: '0.5rem' }} 
        />
        <span className="ml-2 text-sm text-gray-500">{rowData.progress}%</span>
      </div>
    );
  };

  const actionTemplate = (rowData) => {
    return (
      <Button 
        icon={<ChevronRight size={16} />} 
        className="p-button-text p-button-rounded" 
        onClick={() => handleProjectClick(rowData)}
        aria-label="View ships"
      />
    );
  };

  const shipActionTemplate = (rowData) => {
    return (
      <Button 
        icon={<ChevronRight size={16} />} 
        className="p-button-text p-button-rounded" 
        onClick={() => handleShipClick(rowData)}
        aria-label="View tasks"
      />
    );
  };

  // New template for subtask details action
  const subtaskDetailsActionTemplate = (rowData) => {
    return (
      <Button 
        label={windowSize.width < 768 ? "Details" : "View Details"}
        className="p-button-outlined p-button-secondary text-xs sm:text-sm p-2" 
        onClick={() => handleSubtaskDetailsClick(rowData)}
        loading={subtaskDetailsLoading && selectedSubtask?.id === rowData.id}
        aria-label="View subtask details"
      />
    );
  };

  const getResponsiveColumns = () => {
    return [
      { field: 'name', header: 'Project', sortable: true },
      { field: 'progress', header: 'Progress', body: progressTemplate, sortable: true },
      { body: actionTemplate, style: { width: '3rem' } }
    ];
  };

  const shipsDialogFooter = (
    <div className="flex justify-between items-center">
      <button
        className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-gray-700 text-sm font-medium"
        onClick={() => setShowShipsDialog(false)}
      >
        Back
      </button>
      <Button 
        icon={isFullView ? <Minimize2 size={16} /> : <Maximize2 size={16} />} 
        className="p-button-text p-1" 
        onClick={toggleFullView} 
        tooltip={isFullView ? "Minimize" : "Maximize"}
      />
    </div>
  );

  const tasksDialogFooter = (
    <div className="flex justify-between items-center">
      <button
        className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-gray-700 text-sm font-medium"
        onClick={() => {
          setShowYardsDialog(false);
          setShowShipsDialog(true);
        }}
      >
        Back
      </button>
      <Button 
        icon={isFullView ? <Minimize2 size={16} /> : <Maximize2 size={16} />} 
        className="p-button-text p-1" 
        onClick={toggleFullView} 
        tooltip={isFullView ? "Minimize" : "Maximize"}
      />
    </div>
  );

  const columns = getResponsiveColumns();
  const dialogDimensions = getDialogDimensions();

  const onPageChange = (event) => {
    const newPage = event.page + 1;
    setCurrentPage(newPage);
  };

  return (
    <div className="primereact-reset card">
      <ReusableTable
        title="Active Projects"
        data={projects}
        columnFields={columns}
        rows={rowsPerPage}
        colorScheme={colorScheme}
        loading={loading}
        hideColumnSelector={true}
        totalRecords={totalPages * rowsPerPage}
        first={(currentPage - 1) * rowsPerPage}
        onPageChange={onPageChange}
        onSearchChange={handleTableSearch}
        searchValue={search}
      />

      {showShipsDialog && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b flex justify-between items-center bg-indigo-900">
            <div>
              <h3 className="text-xl font-semibold text-white">Active Ships</h3>
              <p className="text-sm text-white mt-1">{`Overview of all active ships for this ${selectedProjectState?.name}`}</p>
            </div>
            <button
              onClick={() => setShowShipsDialog(false)}
              className="text-white hover:text-gray-300"
            >
              <X size={24} />
            </button>
          </div>
          <div className="px-6 overflow-auto">
            <ReusableTable
                title="Ships"
                data={selectedProjectState?.ships}
                columnFields={[
                  { field: 'name', header: 'Ship Name', sortable: true },
                  { field: 'progress', header: 'Progress', body: progressTemplate, sortable: true },
                  { body: shipActionTemplate, style: { width: '3rem' } }
                ]}
                rows={windowSize.height < 768 ? 3 : 5}
                colorScheme={colorScheme}
                loading={loading}
                hideColumnSelector={true}
              />
          </div>
        </div>
      </div>)}

      {showYardsDialog && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b flex justify-between items-center bg-indigo-900">
            <div className="flex">
              <button onClick={() => {setShowShipsDialog(true); setShowYardsDialog(false);}}
                className="text-white bg-indigo-700 rounded-lg p-2 hover:text-gray-300 mr-4"
                title="Back to Ships"
              >
                <ChevronLeft size={24} />
              </button>
              <div>
                <h3 className="text-xl font-semibold text-white">Active Tasks</h3>
                <p className="text-sm text-white mt-1">{`Overview of all active tasks for this ${selectedShipState?.name} of ${selectedProjectState?.name}`}</p>
              </div>
            </div>
            <button
              onClick={() => setShowYardsDialog(false)}
              className="text-white hover:text-gray-300"
            >
              <X size={24} />
            </button>
          </div>
          <div className="px-6 overflow-auto">
            <ReusableTable
                title="Tasks"
                data={selectedShipState?.yards?.slice(tasksFirst, tasksFirst + tasksRows) || []}
                columnFields={windowSize.width < 640 ? 
                  [
                    { 
                      field: 'name', 
                      header: 'Task Name', 
                      sortable: true,
                      body: (rowData) => (
                        <Button 
                          label={rowData.name}
                          className="p-button-link text-xs sm:text-sm task-name-cell" 
                          onClick={() => handleYardClick(rowData)} 
                        />
                      )
                    },
                    { field: 'progress', header: 'Progress', body: progressTemplate, sortable: true },
                    { 
                      body: (rowData) => (
                        <Button 
                          icon={<ChevronRight size={16} />}
                          className="p-button-rounded p-button-text" 
                          onClick={() => handleYardClick(rowData)} 
                        />
                      ),
                      style: { width: '3rem' }
                    }
                  ] : 
                  [
                    { 
                      field: 'name', 
                      header: 'Task Name', 
                      sortable: true,
                      body: (rowData) => (
                        <Button 
                          label={rowData.name}
                          className="p-button-link" 
                          onClick={() => handleYardClick(rowData)} 
                        />
                      )
                    },
                    { field: 'progress', header: 'Progress', body: progressTemplate, sortable: true },
                    { 
                      body: (rowData) => (
                        <Button 
                          label={windowSize.width < 768 ? "View" : "View Details"}
                          className="p-button-outlined p-button-secondary text-xs sm:text-sm p-2" 
                          onClick={() => handleYardClick(rowData)} 
                        />
                      ),
                      style: { width: windowSize.width < 768 ? '5rem' : '10rem' }
                    }
                  ]
                }
                rows={tasksRows}
                colorScheme={colorScheme}
                loading={loading}
                hideColumnSelector={true}
                totalRecords={tasksTotalRecords}
                first={tasksFirst}
                onPageChange={onTasksPageChange}
              />
          </div>
        </div>
      </div>)}

      {showSubtasksDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-indigo-900">
              <div className="flex">
                <button 
                  onClick={() => {
                    setShowSubtasksDialog(false);
                    setShowYardsDialog(true);
                  }}
                  className="text-white bg-indigo-700 rounded-lg p-2 hover:text-gray-300 mr-4"
                  title="Back to Tasks"
                >
                  <ChevronLeft size={24} />
                </button>
                <div>
                  <h3 className="text-xl font-semibold text-white">Active Subtasks</h3>
                  <p className="text-sm text-white mt-1">
                    {`Overview of subtasks for ${selectedTaskState?.name} (${selectedShipState?.name})`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSubtasksDialog(false)}
                className="text-white hover:text-gray-300"
              >
                <X size={24} />
              </button>
            </div>
            <div className="px-6 overflow-auto">
              <ReusableTable
                title="Subtasks"
                data={selectedTaskState?.subtasks?.slice(subtasksFirst, subtasksFirst + subtasksRows) || []}
                columnFields={[
                  { 
                    field: 'name', 
                    header: 'Subtask Name', 
                    sortable: true,
                    body: (rowData) => (
                      <span className="text-sm font-medium text-gray-800">
                        {rowData.name}
                      </span>
                    )
                  },
                  { 
                    field: 'progress', 
                    header: 'Progress', 
                    body: progressTemplate, 
                    sortable: true 
                  },
                  {
                    body: subtaskDetailsActionTemplate,
                    style: { width: windowSize.width < 768 ? '6rem' : '10rem' }
                  }
                ]}
                rows={subtasksRows}
                colorScheme={colorScheme}
                loading={loading}
                hideColumnSelector={true}
                totalRecords={subtasksTotalRecords}
                first={subtasksFirst}
                onPageChange={(event) => {
                  setSubtasksFirst(event.first);
                  setSubtasksRows(event.rows);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* New Subtask Details Dialog */}
      {showSubtaskDetailsDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-indigo-900">
              <div className="flex">
                <button 
                  onClick={() => {
                    setShowSubtaskDetailsDialog(false);
                  }}
                  className="text-white bg-indigo-700 rounded-lg p-2 hover:text-gray-300 mr-4"
                  title="Back to Subtasks"
                >
                  <ChevronLeft size={24} />
                </button>
                <div>
                  <h3 className="text-xl font-semibold text-white">Subtask Details</h3>
                  <p className="text-sm text-white mt-1">
                    {`Details for ${selectedSubtask?.name}`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSubtaskDetailsDialog(false)}
                className="text-white hover:text-gray-300"
              >
                <X size={24} />
              </button>
            </div>
            <div className="px-6 py-6 overflow-auto">
              {subtaskDetailsLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="text-gray-600">Loading subtask details...</div>
                </div>
              ) : subtaskDetails.length === 0 ? (
                <div className="flex justify-center items-center py-8">
                  <div className="text-center">
                    <div className="text-gray-500 text-lg mb-2">No Data Available</div>
                    <div className="text-gray-400 text-sm">
                      No details available for this subtask at the moment.
                    </div>
                  </div>
                </div>
              ) : (
                <ReusableTable
                  title="Subtask Details"
                  data={subtaskDetails}
                  columnFields={[
                    { 
                      field: 'sub_task_name', 
                      header: 'Sub Task Name', 
                      sortable: true 
                    },
                    { 
                      field: 'progress_percentage', 
                      header: 'Progress', 
                      body: (rowData) => (
                        <div className="flex items-center">
                          <ProgressBar 
                            value={rowData.progress_percentage} 
                            showValue={false} 
                            style={{ width: windowSize.width < 640 ? '5rem' : '10rem', height: '0.5rem' }} 
                          />
                          <span className="ml-2 text-sm text-gray-500">{rowData.progress_percentage}%</span>
                        </div>
                      ),
                      sortable: true 
                    }
                  ]}
                  rows={10}
                  colorScheme={colorScheme}
                  loading={false}
                  hideColumnSelector={true}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReusableProjectTable;